import { isNextStepDynamic } from 'components/form-wizards/basic-form/utils';
import get from 'lodash/get';
import { LogError } from 'utils/logging/LogError';
import { getValidQuestions, isInvalidValue } from 'utils/formValuesUtils';
import { getDerivedFormValue } from 'components/form-wizards/basic-form/handleTaxonomyPreSelect';
import getVisibleQuestions from 'utils/form-utils/getVisibleQuestions';
import { QUESTION_IDS } from 'consts';
import moveToNextStep from './moveToNextStep';
import handleDynamicOptions from '../utils/handleDynamicOptions';

/**
 * @summary - this is to initialize the form state
 * @param {Object} state - the state
 * @param {Object} formConfigs - form configs from the API
 */
export default function initializeState(
  state,
  formConfigs,
  partialGlobalContext
) {
  const { userData, taxonomyValues, microSiteTaxonomyMap } =
    partialGlobalContext;
  const currentStepIndex = formConfigs.initialStepIndex || 0;
  const step = formConfigs.steps && formConfigs.steps[currentStepIndex];
  const _formValues = { ...state.formValues };

  if (!step) {
    LogError(
      'BasicFormWizard initializeState Error: This form does not have steps',
      { formId: formConfigs.id }
    );

    return state;
  }

  const allQuestions = formConfigs.steps
    .map(({ questions }) => questions)
    .flat();

  const fieldNameMap = allQuestions.reduce(
    (map, question) => ({ ...map, [question.name]: question }),
    {}
  );

  const allDynamicOptionQuestions = allQuestions.filter(
    (question) =>
      get(question, 'dependency.DYNAMIC_OPTIONS') ||
      get(question, 'dependency.DYNAMIC_VISIBILITY')
  );

  const allQuestionsInForm = allQuestions.reduce(
    (map, question) => ({ ...map, [question.id]: question }),
    {}
  );

  /**
   * We may want some form values to be pre-selected
   * we look for question[n].options[n].isSelected
   */
  allQuestions.forEach((question) => {
    if (isInvalidValue(_formValues[question.name])) {
      (question.options || []).forEach((option) => {
        if (option.isSelected || question.options.length === 1) {
          _formValues[question.name] = option;
        }
      });
    }
  });

  const initialDerivedFormValues = getDerivedFormValue(
    userData,
    taxonomyValues,
    allQuestionsInForm,
    microSiteTaxonomyMap
  );

  const initialState = {
    ...state,
    formValues: { ..._formValues, ...initialDerivedFormValues },
    title: step.title || formConfigs.title,
    subTitle: step.subTitle || formConfigs.subTitle,
    currentStepIndex,
    isLastStep:
      formConfigs.steps && formConfigs.steps.length - 1 === currentStepIndex,
    isFirstStep: currentStepIndex === 0,
    currentQuestions: getValidQuestions(step.questions, {}, allQuestionsInForm),
    totalSteps: formConfigs?.steps.length,
    ctaText: step.ctaText || 'Continue',
    allQuestionsInForm,
    allDynamicOptionQuestions,
    formConfigs,
    fieldNameMap,
    formStatus: {
      ...state.formStatus,
      isInitializing: false,
      isDirty: false,
      isFormResumeEligible: false,
    },
    isNextStepDynamic: isNextStepDynamic(
      {
        ...state,
        formConfigs,
      },
      0
    ),
  };

  const visibleQuestions = getVisibleQuestions(
    initialState.currentQuestions,
    initialState
  );

  const optionsToReq = handleDynamicOptions({
    allQuestionsInForm,
    allDynamicOptionQuestions,
    formValues: initialState.formValues,
    fieldNameMap,
  });

  const isOnlySubject =
    optionsToReq &&
    optionsToReq.requestedOptions.length === 1 &&
    optionsToReq.requestedOptions[0] === QUESTION_IDS.SUBJECT_0F_INTEREST;

  if (optionsToReq && !isOnlySubject) {
    // On click portal we abuse the dyanmic options. That was a hack that need to be removed
    if (formConfigs.steps.length > 1) {
      LogError('initializeState on server: DynamicOptions not supported', {
        description: `Question Ids: ${optionsToReq.requestedOptions
          .map((option) => option.questionId)
          .join(', ')}`,
      });
    }

    /* BY DESIGN! we do not support server side rendering of dynamic options,
     * so we reset form values and return
     */
    initialState.formValues = _formValues;
    return initialState;
  }

  if (visibleQuestions.length === 0) {
    return moveToNextStep(initialState);
  }

  return initialState;
}
